// WaitlistAdminPage.tsx - Admin tab: waitlist mode toggle, pending signups,
// approve/dismiss, and invite-entrant ('member' tier) promotion.
import React, { useCallback, useEffect, useMemo, useState } from "react";

type Row = {
  id: string;
  user_id: string;
  email: string;
  note: string | null;
  server: string | null;
  faction: string | null;
  guild_name: string | null;
  guild_rank: string | null;
  status: string;
  created_at: string;
  approved_at: string | null;
  display_name: string;
  discord_id: string;
  access_tier: string;
};

type Counts = {
  pending: number;
  approved: number;
  dismissed: number;
  waitlistedUsersWithoutSignup: number;
};

type MemberRow = { id: string; display_name: string; created_at: string };

const STATUSES = ["pending", "approved", "dismissed"] as const;

type SortKey = "display_name" | "email" | "server" | "guild_name" | "note" | "created_at";
type SortDir = "asc" | "desc";

/** Column header label -> the Row field it sorts on. */
const SORT_COLUMNS: { key: SortKey; label: string }[] = [
  { key: "display_name", label: "Discord" },
  { key: "email", label: "Email" },
  { key: "server", label: "Server" },
  { key: "guild_name", label: "Guild" },
  { key: "note", label: "Note" },
  { key: "created_at", label: "Signed up" },
];

/** Blank cells always sink to the bottom, whichever way the column is sorted. */
function compareRows(a: Row, b: Row, key: SortKey, dir: SortDir): number {
  const flip = dir === "asc" ? 1 : -1;
  if (key === "created_at") {
    return flip * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  }
  const av = (a[key] || "").trim();
  const bv = (b[key] || "").trim();
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return flip * av.localeCompare(bv, undefined, { sensitivity: "base" });
}

export default function WaitlistAdminPage() {
  const [modeEnabled, setModeEnabled] = useState<boolean | null>(null);
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("pending");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<Row[]>([]);
  const [counts, setCounts] = useState<Counts | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [membersOpen, setMembersOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [expandedNote, setExpandedNote] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [rows, sortKey, sortDir]
  );

  // Same column flips direction, a new column starts newest/A-Z first.
  const sortBy = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "created_at" ? "desc" : "asc");
    }
  };

  const load = useCallback(async () => {
    try {
      const [listRes, modeRes, memberRes] = await Promise.all([
        fetch(`/api/admin/waitlist?status=${status}&q=${encodeURIComponent(q)}`, { credentials: "include" }),
        fetch("/api/admin/waitlist-mode", { credentials: "include" }),
        fetch("/api/admin/waitlist/members", { credentials: "include" }),
      ]);
      if (listRes.ok) {
        const data = await listRes.json();
        setRows(data.rows || []);
        setCounts(data.counts || null);
      }
      if (modeRes.ok) setModeEnabled((await modeRes.json()).enabled === true);
      if (memberRes.ok) setMembers((await memberRes.json()).rows || []);
    } catch (e) {
      console.error("waitlist admin load error", e);
    }
  }, [status, q]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMode = async () => {
    setConfirmToggle(false);
    setBusy(true);
    try {
      const res = await fetch("/api/admin/waitlist-mode", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ enabled: !modeEnabled }),
      });
      if (res.ok) setModeEnabled((await res.json()).enabled === true);
    } finally {
      setBusy(false);
    }
  };

  const act = async (userId: string, action: "approve" | "dismiss") => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/waitlist/${userId}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      if (res.ok) {
        setRows((prev) => prev.filter((r) => r.user_id !== userId));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  const approveSelected = async () => {
    setBusy(true);
    try {
      const ids = Array.from(selected);
      for (const id of ids) {
        await fetch(`/api/admin/waitlist/${id}/approve`, { method: "POST", credentials: "include" });
      }
      setSelected(new Set());
      load();
    } finally {
      setBusy(false);
    }
  };

  const promoteMember = async (userId: string) => {
    setBusy(true);
    try {
      const res = await fetch(`/api/admin/waitlist/${userId}/approve`, { method: "POST", credentials: "include" });
      if (res.ok) setMembers((prev) => prev.filter((m) => m.id !== userId));
    } finally {
      setBusy(false);
    }
  };

  const toggleSelect = (userId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header + mode toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-black tracking-tight text-[var(--c-ink)]">Waitlist</h1>
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-[var(--c-ink-3)]">Waitlist mode</span>
          {confirmToggle ? (
            <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5">
              <span className="text-xs text-amber-800 dark:text-amber-200">
                {modeEnabled
                  ? "Turn OFF? New signups get instant full access again."
                  : "Turn ON? New cold signups will be waitlisted."}
              </span>
              <button onClick={toggleMode} disabled={busy} className="text-xs font-bold text-amber-700 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300">Confirm</button>
              <button onClick={() => setConfirmToggle(false)} className="text-xs text-[var(--c-ink-3)] hover:text-[var(--c-ink-2)]">Cancel</button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmToggle(true)}
              disabled={modeEnabled === null || busy}
              className={`relative h-7 w-14 rounded-full transition-colors ${modeEnabled ? "bg-amber-500" : "bg-[var(--c-sunken)]"}`}
              title={modeEnabled ? "Waitlist mode is ON" : "Waitlist mode is OFF"}
            >
              <span
                className={`absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-all ${modeEnabled ? "left-7" : "left-0.5"}`}
              />
            </button>
          )}
          <span className={`text-xs font-bold ${modeEnabled ? "text-amber-700 dark:text-amber-400" : "text-[var(--c-ink-3)]"}`}>
            {modeEnabled === null ? "..." : modeEnabled ? "ON" : "OFF"}
          </span>
        </div>
      </div>

      {/* Counts */}
      {counts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Pending", value: counts.pending, accent: "text-amber-700 dark:text-amber-400" },
            { label: "Approved", value: counts.approved, accent: "text-emerald-700 dark:text-emerald-400" },
            { label: "Dismissed", value: counts.dismissed, accent: "text-[var(--c-ink-3)]" },
            { label: "No contact info", value: counts.waitlistedUsersWithoutSignup, accent: "text-sky-700 dark:text-sky-400" },
          ].map((c) => (
            <div key={c.label} className="rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] px-4 py-3">
              <div className={`text-2xl font-black ${c.accent}`}>{c.value}</div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--c-ink-3)]">{c.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-[var(--c-line)] overflow-hidden">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-4 py-2 text-xs font-bold capitalize transition-colors ${
                status === s ? "bg-[var(--c-accent)] text-[var(--c-ink-on-fill)]" : "bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, server or guild..."
          className="flex-1 min-w-[200px] rounded-lg bg-[var(--c-sunken)] border border-[var(--c-line)] focus:border-amber-500/60 focus:outline-none px-4 py-2 text-sm text-[var(--c-ink)] placeholder-white/25"
        />
        {selected.size > 0 && (
          <button
            onClick={approveSelected}
            disabled={busy}
            className="rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 px-4 py-2 text-xs font-bold text-black"
          >
            Approve selected ({selected.size})
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-[var(--c-ink-3)] border-b border-[var(--c-line)]">
              {status === "pending" && <th className="px-4 py-3 w-8"></th>}
              {SORT_COLUMNS.map((c) => {
                const active = sortKey === c.key;
                return (
                  <th
                    key={c.key}
                    className="px-4 py-3"
                    aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      onClick={() => sortBy(c.key)}
                      className={`group inline-flex items-center gap-1 font-bold uppercase tracking-wider transition-colors ${
                        active ? "text-amber-700 dark:text-amber-400" : "hover:text-[var(--c-ink-2)]"
                      }`}
                      title={`Sort by ${c.label}`}
                    >
                      {c.label}
                      <span className={active ? "" : "opacity-0 group-hover:opacity-40"}>
                        {active && sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    </button>
                  </th>
                );
              })}
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-[var(--c-ink-4)] text-sm">
                  No {status} signups{q ? " matching your search" : ""}.
                </td>
              </tr>
            )}
            {sortedRows.map((r) => (
              <tr key={r.id} className="border-b border-[var(--c-line)] hover:bg-[var(--c-bg-veil)]">
                {status === "pending" && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(r.user_id)}
                      onChange={() => toggleSelect(r.user_id)}
                      className="accent-amber-500"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-[var(--c-ink)]">{r.display_name}</td>
                <td className="px-4 py-3 text-amber-700/90 dark:text-amber-300/90">{r.email}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {r.server ? (
                    <span className="text-[var(--c-ink-2)]">
                      {r.server}
                      {r.faction && (
                        <span className={`ml-1.5 text-[10px] font-bold uppercase ${
                          r.faction === "Alliance" ? "text-sky-700 dark:text-sky-400" : r.faction === "Horde" ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"
                        }`}>
                          {r.faction}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[var(--c-ink-4)]">-</span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {r.guild_name ? (
                    <span className="text-[var(--c-ink-2)]">
                      {r.guild_name}
                      {r.guild_rank && (
                        <span className={`ml-1.5 text-[10px] font-bold uppercase ${
                          r.guild_rank === "Guild Master" ? "text-amber-700 dark:text-amber-400"
                            : r.guild_rank === "Officer" ? "text-emerald-700 dark:text-emerald-400"
                            : "text-[var(--c-ink-3)]"
                        }`}>
                          {r.guild_rank === "Guild Master" ? "GM" : r.guild_rank}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-[var(--c-ink-4)]">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--c-ink-3)] max-w-[240px]">
                  {r.note ? (
                    <button
                      onClick={() => setExpandedNote(expandedNote === r.id ? null : r.id)}
                      className="text-left hover:text-[var(--c-ink)]"
                      title="Click to expand"
                    >
                      {expandedNote === r.id ? r.note : r.note.length > 60 ? r.note.slice(0, 60) + "..." : r.note}
                    </button>
                  ) : (
                    <span className="text-[var(--c-ink-4)]">-</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[var(--c-ink-3)] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.status !== "approved" && (
                    <button
                      onClick={() => act(r.user_id, "approve")}
                      disabled={busy}
                      className="rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25 px-3 py-1.5 text-xs font-bold mr-2"
                    >
                      Approve
                    </button>
                  )}
                  {r.status === "pending" && (
                    <button
                      onClick={() => act(r.user_id, "dismiss")}
                      disabled={busy}
                      className="rounded-md bg-[var(--c-sunken)] text-[var(--c-ink-3)] hover:bg-[var(--c-sunken-2)] px-3 py-1.5 text-xs font-bold"
                    >
                      Dismiss
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Invite entrants ('member' tier) */}
      <div className="rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)]">
        <button
          onClick={() => setMembersOpen((o) => !o)}
          className="w-full flex items-center justify-between px-5 py-4 text-left"
        >
          <div>
            <span className="text-sm font-bold text-[var(--c-ink)]">Invite entrants</span>
            <span className="ml-2 text-xs text-[var(--c-ink-3)]">
              Joined via guild or PUG invite. Full access except guild creation.
            </span>
          </div>
          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">{members.length} {membersOpen ? "▲" : "▼"}</span>
        </button>
        {membersOpen && (
          <div className="border-t border-[var(--c-line)] divide-y divide-[var(--c-line)]">
            {members.length === 0 && (
              <div className="px-5 py-6 text-center text-[var(--c-ink-4)] text-sm">No member-tier users.</div>
            )}
            {members.map((m) => (
              <div key={m.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <span className="text-sm font-semibold text-[var(--c-ink)]">{m.display_name}</span>
                  <span className="ml-2 text-xs text-[var(--c-ink-3)]">since {new Date(m.created_at).toLocaleDateString()}</span>
                </div>
                <button
                  onClick={() => promoteMember(m.id)}
                  disabled={busy}
                  className="rounded-md bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25 px-3 py-1.5 text-xs font-bold"
                >
                  Promote to full
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
